<script>
  import { onMount } from 'svelte';
  import { COLORS, PADDING, clearCanvas } from './chartUtils.js';
  import { formatSignedPercent } from '../../../lib/strategyResult.js';

  // Histogram of monthly returns: { bins: [edges], counts: [n] } where bins.length === counts.length + 1
  // (bin edges, fractions). New Canvas grammar (value-axis x, count-axis y) — the data arrives computed
  // server-side; this is one of the previously-deferred optional charts, now built.
  export let data = { bins: [], counts: [] };

  let canvas;
  let ctx;
  let container;
  let width = 600;
  let height = 200;
  let hoverIndex = -1;

  $: bins = (data && data.bins) || [];
  $: counts = (data && data.counts) || [];
  // Valid when there's at least one bin with a defined edge pair and a non-degenerate width.
  $: hasData = counts.length >= 1 && bins.length === counts.length + 1 && bins[bins.length - 1] > bins[0];
  $: maxCount = counts.length ? Math.max(...counts, 1) : 1;
  $: summary = hasData
    ? `Monthly return distribution: ${counts.reduce((a, b) => a + b, 0)} months across ` +
      `${counts.length} bins from ${formatSignedPercent(bins[0])} to ${formatSignedPercent(bins[bins.length - 1])}.`
    : 'Return distribution: insufficient data.';

  function resize() {
    if (!container) return;
    width = Math.max(200, container.offsetWidth);
    height = 200;
  }

  function draw() {
    if (!ctx || !hasData) return;
    clearCanvas(ctx, width, height);
    const min = bins[0];
    const max = bins[bins.length - 1];
    const plotW = width - PADDING.left - PADDING.right;
    const plotH = height - PADDING.top - PADDING.bottom;
    const xOf = (v) => PADDING.left + ((v - min) / (max - min)) * plotW;
    const yOf = (c) => PADDING.top + (1 - c / maxCount) * plotH;

    // Y gridlines (counts).
    ctx.strokeStyle = COLORS.grid;
    ctx.fillStyle = COLORS.label;
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';
    const rows = 4;
    for (let i = 0; i <= rows; i++) {
      const c = Math.round((maxCount * i) / rows);
      const y = yOf(c);
      ctx.beginPath();
      ctx.moveTo(PADDING.left, y);
      ctx.lineTo(width - PADDING.right, y);
      ctx.stroke();
      ctx.fillText(String(c), width - 5, y + 3);
    }

    // Bars: one per bin, spanning [bins[i], bins[i+1]].
    const zeroX = min <= 0 && max >= 0 ? xOf(0) : null;
    counts.forEach((c, i) => {
      const x0 = xOf(bins[i]);
      const x1 = xOf(bins[i + 1]);
      const y = yOf(c);
      const w = Math.max(1, x1 - x0 - 1);
      // Color by the bin's center sign (a return distribution reads gain/loss).
      const center = (bins[i] + bins[i + 1]) / 2;
      ctx.fillStyle = i === hoverIndex ? '#e8e9ea' : (center >= 0 ? COLORS.gain : COLORS.loss);
      ctx.fillRect(x0, y, w, PADDING.top + plotH - y);
    });

    // Zero reference line.
    if (zeroX !== null) {
      ctx.strokeStyle = COLORS.label;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(zeroX, PADDING.top);
      ctx.lineTo(zeroX, height - PADDING.bottom);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // X labels at the extremes + zero.
    ctx.fillStyle = COLORS.label;
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(formatSignedPercent(min), xOf(min), height - 6);
    ctx.fillText(formatSignedPercent(max), xOf(max), height - 6);
    if (zeroX !== null) ctx.fillText('0%', zeroX, height - 6);
  }

  $: if (ctx && canvas && hasData && width && height && counts && hoverIndex >= -1) draw();

  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const min = bins[0];
    const max = bins[bins.length - 1];
    const plotW = width - PADDING.left - PADDING.right;
    const v = min + ((x - PADDING.left) / plotW) * (max - min);
    hoverIndex = counts.findIndex((_, i) => v >= bins[i] && v <= bins[i + 1]);
  }
  function onLeave() { hoverIndex = -1; }

  let liveMsg = '';
  function onKey(e) {
    if (!hasData) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const start = hoverIndex < 0 ? 0 : hoverIndex;
      hoverIndex = Math.max(0, Math.min(counts.length - 1, start + (e.key === 'ArrowRight' ? 1 : -1)));
      liveMsg = `${formatSignedPercent(bins[hoverIndex])} to ${formatSignedPercent(bins[hoverIndex + 1])}: ${counts[hoverIndex]} months`;
    }
  }

  // Lazy 2D-context acquisition: the canvas renders inside {#if hasData}, so it may not exist at
  // mount (empty data — acquiring unconditionally threw and broke the whole reactive graph) and may
  // APPEAR later when data arrives. Acquire the context whenever the canvas binds.
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  onMount(() => {
    if (!canvas) return; // not rendered (empty state) — nothing to wire up
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    let pendingDraw;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && ctx && canvas) {
          clearTimeout(pendingDraw);
          pendingDraw = setTimeout(() => { resize(); draw(); }, 50);
        }
      });
    }, { threshold: 0.1 });
    if (container) observer.observe(container);
    return () => { window.removeEventListener('resize', onResize); observer.disconnect(); clearTimeout(pendingDraw); };
  });
</script>

<div class="bt-chart" bind:this={container}>
  <h4 class="bt-chart-title">Monthly Return Distribution</h4>
  {#if hasData}
    <canvas
      bind:this={canvas}
      width={width}
      height={height}
      tabindex="0"
      aria-label={summary}
      on:mousemove={onMove}
      on:mouseleave={onLeave}
      on:keydown={onKey}
    ></canvas>
    <div class="bt-chart-live" aria-live="polite">{liveMsg}</div>
    <details class="bt-chart-data">
      <summary>View data as table</summary>
      <table>
        <thead><tr><th>Range</th><th>Months</th></tr></thead>
        <tbody>
          {#each counts as c, i}
            <tr><td>{formatSignedPercent(bins[i])} – {formatSignedPercent(bins[i + 1])}</td><td>{c}</td></tr>
          {/each}
        </tbody>
      </table>
    </details>
  {:else}
    <div class="empty-state"><p class="empty-state-description">Insufficient data for this chart.</p></div>
  {/if}
</div>

<style>
  @import './charts.css';
</style>
